import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Card,
  Button,
  IconButton,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Stack,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Autocomplete,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMeta } from '../../../hooks/useMeta';
import {
  fetchContactsEntries,
  addContactsEntry,
  updateContactsEntry,
  deleteContactsEntry,
  fetchCenters
} from '../../../Services/FirebaseUtils';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const Contacts = () => {
  const { cities } = useMeta();
  const [searchName, setSearchName] = useState('');
  const [searchOperator, setSearchOperator] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [newCenter, setNewCenter] = useState('');
  const [availableCenters, setAvailableCenters] = useState([]);
  const [availableOperators, setAvailableOperators] = useState([]);
  const [allCenters, setAllCenters] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    designation: '',
    cities: [],
    centers: [],
    operator: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    phone: '',
    email: '',
    designation: '',
    cities: '',
    centers: '',
    operator: ''
  });

  useEffect(() => {
    fetchData();
    loadMetaData();
  }, []);

  useEffect(() => {
    if (formData.operator && allCenters.length > 0) {
      let filteredCenters;
      filteredCenters = ['All Centers', ...allCenters.map(center => center.centerName)];
      filteredCenters = ['All Centers', ...allCenters
        .filter(center => center.operatorName === formData.operator)
        .map(center => center.centerName)];
      setAvailableCenters(filteredCenters);
      // Only clear centers if not "All Centers" is selected
      if (!formData.centers.includes('All Centers')) {
        setFormData(prev => ({
          ...prev,
          centers: prev.centers.filter(center => filteredCenters.includes(center))
        }));
      }
    }
  }, [formData.operator, allCenters]);

  const loadMetaData = async () => {
    try {
      const data = await fetchCenters();
      setAllCenters(data.centers || []);
      setAvailableCenters([]);
      setAvailableOperators([...data.operators.map(op => op.operatorName)] || []);
    } catch (error) {
      console.error('Error loading metadata:', error);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: '',
      phone: '',
      email: '',
      designation: '',
      cities: '',
      centers: '',
      operator: ''
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }

    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Enter a valid 10-digit phone number';
      isValid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
      isValid = false;
    }

    if (!formData.designation.trim()) {
      newErrors.designation = 'Designation is required';
      isValid = false;
    }

    if (!formData.cities.length) {
      newErrors.cities = 'At least one city is required';
      isValid = false;
    }

    if (!formData.centers.length) {
      newErrors.centers = 'At least one center is required';
      isValid = false;
    }

    if (!formData.operator) {
      newErrors.operator = 'Operator is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const fetchData = async () => {
    setLoading(true);
    const result = await fetchContactsEntries();
    if (result.success) {
      setData(result.entries);
    } else {
      setSnackbar({
        open: true,
        message: 'Error fetching data: ' + result.error,
        severity: 'error'
      });
    }
    setLoading(false);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the errors in the form',
        severity: 'error'
      });
      return;
    }

    try {
      const saveData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        designation: formData.designation,
        cities: formData.cities,
        centers: formData.centers,
        operator: formData.operator
      };

      let result;
      if (selectedRow && selectedRow.id) {
        result = await updateContactsEntry(selectedRow.id, saveData);
      } else {
        result = await addContactsEntry(saveData);
      }

      if (result.success) {
        setSnackbar({
          open: true,
          message: `Successfully ${selectedRow ? 'updated' : 'added'} contact`,
          severity: 'success'
        });
        await fetchData();
        handleDialogClose();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      setSnackbar({
        open: true,
        message: 'Error saving contact: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    }
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    console.log('Editing row:', selectedRow);
    if (selectedRow) {
      setFormData({
        name: selectedRow.name || '',
        phone: selectedRow.phone || '',
        email: selectedRow.email || '',
        designation: selectedRow.designation || '',
        cities: selectedRow.cities || [],
        centers: selectedRow.centers || [],
        operator: selectedRow.operator || ''
      });
      setErrors({
        name: '',
        phone: '',
        email: '',
        designation: '',
        cities: '',
        centers: '',
        operator: ''
      });
      setOpenDialog(true);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    try {
      const result = await deleteContactsEntry(selectedRow.id);
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Successfully deleted contact',
          severity: 'success'
        });
        await fetchData();
        handleMenuClose();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error deleting contact: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedRow(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      designation: '',
      cities: [],
      centers: [],
      operator: ''
    });
    setErrors({
      name: '',
      phone: '',
      email: '',
      designation: '',
      cities: '',
      centers: '',
      operator: ''
    });
    setAnchorEl(null);
  };

  const handleAddCenter = (event) => {
    if (event.key === 'Enter' && newCenter.trim()) {
      setFormData({
        ...formData,
        centers: [...formData.centers, newCenter.trim()]
      });
      setNewCenter('');
    }
  };

  const handleRemoveCenter = (centerToRemove) => {
    setFormData({
      ...formData,
      centers: formData.centers.filter(center => center !== centerToRemove)
    });
  };

  const handleCityChange = (event) => {
    const value = event.target.value;
    let newCities;

    if (value.includes('All Cities')) {
      // If "All Cities" is selected, only keep "All Cities"
      newCities = ['All Cities'];
    } else {
      // Remove "All Cities" if other cities are selected
      newCities = value.filter(city => city !== 'All Cities');
    }

    setFormData(prev => ({
      ...prev,
      cities: newCities
    }));
  };

  const handleCenterChange = (event) => {
    const value = event.target.value;
    let newCenters;

    if (value.includes('All Centers')) {
      // If "All Centers" is selected, only keep "All Centers"
      newCenters = ['All Centers'];
    } else {
      // Remove "All Centers" if other centers are selected
      newCenters = value.filter(center => center !== 'All Centers');
    }

    setFormData(prev => ({
      ...prev,
      centers: newCenters
    }));
  };

  const filteredData = data.filter(item => {
    const nameMatch = item.name.toLowerCase().includes(searchName.toLowerCase());
    const operatorMatch = item.operator.toLowerCase().includes(searchOperator.toLowerCase());
    return nameMatch && operatorMatch;
  });

  return (
    <Card sx={{ borderRadius: 1}}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Box sx={{ width: '30%' }}>
            <TextField
              fullWidth
              size="small"
              label="Filter by Name"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', sm: '30%' } }}>
            <TextField
              fullWidth
              size="small"
              label="Filter by Operator"
              value={searchOperator}
              onChange={(e) => setSearchOperator(e.target.value)}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }} />
          <Button
            variant="contained"
            onClick={() => {
              setSelectedRow(null);
              setOpenDialog(true);
              setFormData({
                name: '',
                phone: '',
                email: '',
                designation: '',
                cities: [],
                centers: [],
                operator: ''
              });
              setErrors({
                name: '',
                phone: '',
                email: '',
                designation: '',
                cities: '',
                centers: '',
                operator: ''
              });
            }}
            sx={{
              backgroundColor: '#2193B0',
              '&:hover': {
                backgroundColor: '#1C7F98'
              }
            }}
          >
            Add Contact
          </Button>
        </Box>
        <Box sx={{
          height: "1px",
          backgroundColor: "#e0e0e0",
          width: "calc(100% + 32px)",
          mx: -3,
          mb: 2
        }} />

        <TableContainer >
          <Table sx={{ minWidth: 650 , borderRadius:0 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: (theme) => `${theme.palette.primary.main}08` }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Designation</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Operator</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cities</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Centers</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '60px' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ maxWidth: '200px' }}>
                  {filteredData.map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 }  }}
                    >
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.designation}</TableCell>
                      <TableCell>{item.phone}</TableCell>
                      <TableCell sx={{
                                maxWidth: '150px',
                                m: 0.5,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                              }}>
                                <Tooltip title={item.email} placement="top" arrow>
                                  <span>{item.email}</span>
                                </Tooltip>
                                </TableCell>
                      <TableCell>
                        
                        {item.operator}</TableCell>
                      <TableCell>
                        {item.cities?.map((city, index) => (
                          <Chip
                            key={index}
                            label={city}
                            size="small"
                            sx={{
                              m: 0.5,
                              backgroundColor: '#2193B0',
                              color: 'white'
                            }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        {item.centers?.map((center, index) => (
                          <Tooltip key={index} title={center} placement="top" arrow>
                            <Chip
                              label={center}
                              size="small"
                              sx={{
                                maxWidth: '100px',
                                m: 0.5,
                                backgroundColor: '#2193B0',
                                color: 'white',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                              }}
                            />
                          </Tooltip>
                        ))}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(event) => handleMenuOpen(event, item)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleEdit} sx={{ gap: 1 }}>
            <EditIcon fontSize="small" />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ gap: 1, color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
            Delete
          </MenuItem>
        </Menu>
      </Box>
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedRow ? 'Edit Contact' : 'Add New Contact'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 3,
                '& .MuiFormControl-root': { width: '100%' }
              }}
            >
              <FormControl error={!!errors.name}>
                <TextField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                {errors.name && (
                  <Typography color="error" variant="caption">
                    {errors.name}
                  </Typography>
                )}
              </FormControl>
              <FormControl error={!!errors.designation}>
                <TextField
                  label="Designation"
                  name="designation"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                />
                {errors.designation && (
                  <Typography color="error" variant="caption">
                    {errors.designation}
                  </Typography>
                )}
              </FormControl>
              <FormControl error={!!errors.phone}>
                <TextField
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
                {errors.phone && (
                  <Typography color="error" variant="caption">
                    {errors.phone}
                  </Typography>
                )}
              </FormControl>
              <FormControl error={!!errors.email}>
                <TextField
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
                {errors.email && (
                  <Typography color="error" variant="caption">
                    {errors.email}
                  </Typography>
                )}
              </FormControl>
              <FormControl error={!!errors.operator}>
                <Autocomplete
                  value={formData.operator}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      operator: newValue,
                      centers: []
                    }));
                  }}
                  options={availableOperators}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Operator"

                    />
                  )}

                />
                {errors.operator && (
                  <Typography color="error" variant="caption">
                    {errors.operator}
                  </Typography>
                )}
              </FormControl>

              <FormControl error={!!errors.cities}>
                <InputLabel sx={{
                  color: '#6B6B6B !important',
                  '&.Mui-error': {
                    color: '#6B6B6B !important',
                  },
                }}>Cities</InputLabel>
                <Select
                  multiple
                  value={formData.cities}
                  onChange={handleCityChange}
                  input={<OutlinedInput label="Cities" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          sx={{
                            backgroundColor: '#2193B0',
                            color: 'white',
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                              '&:hover': { color: '#ff4444' },
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#C4C4C4 !important', // hides red border,
                      color: '#C4C4C4',

                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#C4C4C4 !important',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#C4C4C4 !important',
                      color: '#C4C4C4',

                    },
                  }}
                >
                  <MenuItem value="All Cities">All Cities</MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </Select>
                {errors.cities && (
                  <Typography color="error" variant="caption">
                    {errors.cities}
                  </Typography>
                )}
              </FormControl>
            </Box>
            <Box sx={{ mt: 3 }}>
              <FormControl error={!!errors.centers} fullWidth>
                <InputLabel sx={{
                  color: '#6B6B6B !important',
                  '&.Mui-error': {
                    color: '#6B6B6B !important',
                  },
                }}>Centers</InputLabel>
                <Select
                  multiple
                  value={formData.centers}
                  onChange={handleCenterChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          sx={{
                            backgroundColor: '#2193B0',
                            color: 'white',
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                              '&:hover': { color: '#ff4444' }
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                  disabled={!formData.operator}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#C4C4C4 !important', // hides red border
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#C4C4C4 !important',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#C4C4C4 !important',
                    },
                  }}
                >
                  {availableCenters.map((center) => (
                    <MenuItem key={center} value={center}>
                      {center}
                    </MenuItem>
                  ))}
                </Select>
                {errors.centers && (
                  <Typography color="error" variant="caption">
                    {errors.centers}
                  </Typography>
                )}

              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              backgroundColor: '#2193B0',
              '&:hover': {
                backgroundColor: '#1C7F98'
              }
            }}
          >
            {selectedRow ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default Contacts;
